import { useState, useEffect, useCallback } from 'react';
import { shouldUseNativePicker, generateThumbnail } from '../../../Utils/nativeFileUpload';

/**
 * Manages draft attachments for a conversation.
 * Loads and transforms file_uploads from the conversation into Ant Design fileList format.
 * 
 * @param {Object} selectedConversation - The currently selected conversation
 * @returns {Object} { draftAttachments, setDraftAttachments, clearDrafts }
 */
const useDraftAttachments = (selectedConversation) => {
    const [draftAttachments, setDraftAttachments] = useState([]);

    // Load draft attachments from conversation.file_uploads
    useEffect(() => {
        const loadDrafts = async () => {
            const drafts = selectedConversation?.file_uploads || [];
            if (drafts.length > 0) {
                const isNative = shouldUseNativePicker();

                const fileListItems = await Promise.all(drafts.map(async (draft) => {
                    const isImage = draft.mime_type?.startsWith('image/');

                    // For native platforms, generate base64 thumbnail for instant preview
                    // For web, use API's file_url directly (performance optimization)
                    let thumbUrl = null;
                    if (isImage) {
                        if (isNative && draft.file_url) {
                            // Fetch and convert to base64 for native
                            try {
                                const response = await fetch(draft.file_url);
                                const blob = await response.blob();
                                const file = new File([blob], draft.original_filename || 'image.jpg', { type: draft.mime_type });
                                thumbUrl = await generateThumbnail(file, draft.file_url);
                            } catch (e) {
                                // Fallback to URL if thumbnail generation fails
                                thumbUrl = draft.file_url;
                            }
                        } else {
                            // Web: use URL directly
                            thumbUrl = draft.file_url;
                        }
                    }

                    return {
                        uid: `-draft-${draft.id}`,
                        name: draft.original_filename || 'file',
                        status: 'done',
                        url: isImage ? draft.file_url : null,
                        thumbUrl: thumbUrl,
                        fileId: draft.id,
                        fileData: draft,
                        size: draft.size,
                        type: draft.mime_type
                    };
                }));
                setDraftAttachments(fileListItems);
            } else {
                setDraftAttachments([]);
            }
        };

        loadDrafts();
    }, [selectedConversation]);

    const clearDrafts = useCallback(() => {
        setDraftAttachments([]);
    }, []);

    return {
        draftAttachments,
        setDraftAttachments,
        clearDrafts
    };
};

export default useDraftAttachments;
